import React from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, LinearProgress,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDiscoverableDocuments, useTaxReadiness } from "../../../api/fiscalite";

interface Props {
  fleetId: number;
  fiscalYear: number;
  onGoToDocuments: () => void;
}

/**
 * Data completeness.
 *
 * This screen exists because the honest answer to "what do I owe?" is often
 * "we cannot tell you yet". Gaps are ranked by how many vehicles each one
 * blocks, so the user always sees the single highest-leverage action first
 * rather than a flat list of missing fields.
 */
export const Completude: React.FC<Props> = ({ fleetId, fiscalYear, onGoToDocuments }) => {
  const { t } = useTranslation();
  const { readiness, isLoading } = useTaxReadiness(fleetId, fiscalYear);
  const { candidates } = useDiscoverableDocuments(fleetId);

  if (isLoading || !readiness) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  }

  const percent = Math.round(readiness.readiness_ratio * 100);

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t("tax.readinessTitle", "Vehicules pretes au calcul")}
              </Typography>
              <Typography variant="h3" fontWeight={700}>
                {readiness.vehicles_ready} / {readiness.vehicles_total}
              </Typography>
            </Box>
            <Chip
              color={readiness.is_filing_ready ? "success" : percent > 50 ? "warning" : "error"}
              label={`${percent}%`}
            />
          </Stack>
          <LinearProgress variant="determinate" value={percent} sx={{ mt: 2 }} />
        </CardContent>
      </Card>

      {/* Placed ABOVE next_best_action on purpose.

          This is the screen where a customer discovers the module knows
          nothing about their fleet, and telling them to go and upload cartes
          grises they demonstrably uploaded already is the moment the product
          loses them. When the coffre-fort holds documents we can reuse, the
          cheapest available action is not "import" — it is "reuse" — and it
          has to be the first thing offered, not something found later in
          another tab. */}
      {candidates.length > 0 && (
        <Alert
          severity="success"
          action={
            <Button size="small" variant="contained" onClick={onGoToDocuments}>
              {t("tax.repriseGo", "Reprendre ces documents")}
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            {t("tax.repriseAvailable", {
              defaultValue:
                "{{count}} document(s) sont deja dans votre coffre-fort et peuvent completer ces fiches.",
              count: candidates.length,
            })}
          </Typography>
          <Typography variant="caption">
            {t(
              "tax.repriseAvailableHint",
              "Aucun reimport n'est necessaire. Les valeurs extraites vous seront soumises pour validation avant d'alimenter le calcul."
            )}
          </Typography>
        </Alert>
      )}

      {/* One action, named, with its impact quantified. */}
      {readiness.next_best_action && (
        <Alert
          severity="info"
          action={
            readiness.next_best_action.code === "CREATE_PROFILES" ? (
              <Button size="small" onClick={onGoToDocuments}>
                {t("tax.importDocs", "Importer les cartes grises")}
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2" fontWeight={600}>{readiness.next_best_action.message}</Typography>
          <Typography variant="caption">{readiness.next_best_action.hint}</Typography>
        </Alert>
      )}

      {readiness.unverified_count > 0 && (
        <Alert severity="warning">
          {t("tax.unverified", {
            defaultValue:
              "{{count}} fiche(s) fiscale(s) n'ont jamais ete verifiees par un utilisateur. Les montants calcules "
              + "portent un avertissement tant que ce n'est pas fait.",
            count: readiness.unverified_count,
          })}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("tax.gaps", "Donnees manquantes, par impact")}
          </Typography>
          {readiness.gaps.length === 0 ? (
            // "No missing field" is only true once every vehicle is covered.
            // A fleet with no fiscal profile at all also produces an empty gap
            // list - there is nothing to inspect yet - and announcing that the
            // fleet is fully computable in that state is exactly the kind of
            // reassuring falsehood this screen exists to prevent.
            <Typography variant="body2" color="text.secondary">
              {readiness.vehicles_ready === readiness.vehicles_total && readiness.vehicles_total > 0
                ? t("tax.noGaps", "Aucune donnee manquante. Le parc est integralement calculable.")
                : t(
                    "tax.noProfilesYet",
                    "Aucune fiche fiscale n'existe encore : il n'y a donc rien a analyser. Creez les fiches (import des cartes grises ou saisie) pour voir les champs manquants."
                  )}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("tax.field", "Champ")}</TableCell>
                  <TableCell align="right">{t("tax.vehiclesBlocked", "Vehicules bloques")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {readiness.gaps.map((gap) => (
                  <TableRow key={gap.field} hover>
                    <TableCell>{gap.label}</TableCell>
                    <TableCell align="right">
                      <Chip size="small" color="warning" label={gap.vehicles_blocked} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {readiness.blocking_vehicles.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {t("tax.blockingVehicles", "Vehicules concernes")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("tax.vehicle", "Vehicule")}</TableCell>
                  <TableCell>{t("tax.missingFields", "Champs manquants")}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {readiness.blocking_vehicles.map((vehicle) => (
                  <TableRow key={vehicle.vehicle_id} hover>
                    <TableCell>{vehicle.plate ?? `#${vehicle.vehicle_id}`}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {vehicle.missing_fields.map((field) => (
                          <Chip key={field} size="small" variant="outlined" label={field} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" href={`/vehicule/update/${vehicle.vehicle_id}`}>
                        {t("tax.fill", "Completer")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {readiness.blocking_vehicles.length >= 100 && (
              <Typography variant="caption" color="text.secondary">
                {t("tax.truncatedList", "Liste limitee aux 100 premiers vehicules.")}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};
